import React, { useCallback } from 'react';
import { Combobox } from '@headlessui/react';

import { useGeolocation } from '../../../utils/hooks';
import {
  geocodeLocation,
  searchLocations,
} from '../../../utils/serviceCalls/location';
import { ExclamationTriangleIcon, LoadingIndicator, MapPin } from './Icons';
import { SearchCombobox } from './SearchCombobox';

/** @typedef {import('../../../types/location').LocationOption} LocationOption */

// Geolocation config
const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 60000,
};

const CurrentLocationOption = ({
  latitude,
  longitude,
  isLocationDisabled,
  isGettingLocation,
  showCurrentLocation,
}) => {
  if (!showCurrentLocation) {
    return null;
  }

  const currentLocationValue = {
    value: 'Current Location',
    pt: `${latitude},${longitude}`,
    isCurrentLocation: true,
  };

  const getLocationText = () => {
    if (isGettingLocation) return 'Getting your location...';
    if (isLocationDisabled) return 'Location Access Denied';
    return 'Use Current Location';
  };

  const getLocationIcon = () => {
    if (isGettingLocation) return <LoadingIndicator />;
    if (isLocationDisabled) {
      return <ExclamationTriangleIcon />;
    }
    return <MapPin />;
  };

  const base =
    'w-full relative py-3 px-4 border-b border-gray-200 flex items-center gap-3 text-left';
  const disabledCls = 'text-gray-50 cursor-not-allowed bg-gray-05';
  const activeCls = 'bg-blue-05 text-gray-90 cursor-pointer';
  const defaultCls = 'text-gray-90 cursor-pointer hover:bg-blue-05';

  return (
    <Combobox.Option
      value={currentLocationValue}
      disabled={isLocationDisabled || isGettingLocation}
      className={({ active }) =>
        [
          base,
          isLocationDisabled ? disabledCls : active ? activeCls : defaultCls,
          isGettingLocation ? 'opacity-75' : '',
          'disabled:cursor-not-allowed',
        ].join(' ')
      }
    >
      <div className="flex items-center gap-2">
        {getLocationIcon()}
        <div className="flex flex-col">
          <span
            className={`font-sm ${isLocationDisabled ? 'text-gray-90' : 'text-blue-70'}`}
          >
            {getLocationText()}
          </span>
          {isLocationDisabled && (
            <span className="text-xs text-gray-40 mt-0.5">
              Enable location permissions to use this feature
            </span>
          )}
        </div>
      </div>
    </Combobox.Option>
  );
};

/**
 * Get display label for location option
 * @param {LocationOption | null} item - Location option
 * @returns {string} Display label
 */
const getOptionLabel = (item) => {
  if (!item) return '';
  if (item.isCurrentLocation) return 'Current Location';
  return item.highlightedText || item.value || '';
};

/**
 * Get unique key for location option
 * @param {LocationOption | null} item - Location option
 * @returns {string} Unique key
 */
const getOptionKey = (item) => {
  if (!item) return '';
  if (item.isCurrentLocation) return 'current-location';
  return item.pt || item.value || '';
};

const LocationCustomOptions = ({
  geolocation,
  isLocationDisabled,
  isGettingLocation,
  showCurrentLocation,
}) => {
  if (!showCurrentLocation) {
    return null;
  }

  return (
    <CurrentLocationOption
      latitude={geolocation.latitude}
      longitude={geolocation.longitude}
      isLocationDisabled={isLocationDisabled}
      isGettingLocation={isGettingLocation}
      showCurrentLocation={showCurrentLocation}
    />
  );
};

/**
 * Location search input with geolocation support.
 * - Type to search cities/states
 * - Auto-detects current location when available
 * - Smart dropdown behavior based on location permissions
 */
const LocationSearchCombobox = React.forwardRef(
  /**
   * @param {Object} props
   * @param {LocationOption} [props.value] - Selected location value
   * @param {(location: LocationOption | null) => void} [props.onChange] - Callback when location changes
   * @param {string} [props.label] - Input label
   * @param {string} [props.placeholder] - Input placeholder text
   * @param {boolean} [props.disabled] - Whether input is disabled
   * @param {number} [props.debounceWait] - Debounce wait time in ms
   * @param {string} [props.emptyText] - Text shown when no results
   * @param {boolean} [props.showError] - Whether to show error styling
   * @param {boolean} [props.showCurrentLocation] - Whether to show current location option
   * @param {(location: LocationOption | null) => string} [props.getInputDisplayValue] - Function to get input display value
   * @param {string} [props.name] - Input name for react-hook-form
   * @param {React.Ref<HTMLInputElement>} ref
   */
  (props, ref) => {
    const {
      value,
      onChange,
      label = '',
      placeholder = 'Search for a city or state...',
      disabled = false,
      debounceWait = 500,
      emptyText = 'No locations found',
      showError = false,
      showCurrentLocation = true,
      getInputDisplayValue,
      name, // For react-hook-form compatibility
    } = props;
    const geolocation = useGeolocation(GEOLOCATION_OPTIONS);

    const isGettingLocation = geolocation.loading;
    const isLocationUnavailable =
      geolocation.error || (!geolocation.latitude && !geolocation.loading);
    const isLocationDisabled = !!isLocationUnavailable;
    const hasLocationAvailable = !isLocationUnavailable && !geolocation.loading;

    // Extract display string from location object for the input field
    const getInputDisplayValueFallback = useCallback(
      /**
       * @param {LocationOption | string} location
       */
      (location) => {
        if (!location) return '';
        // Handle string values (e.g., when parent stores just city name)
        if (typeof location === 'string') return location;
        if (location.isCurrentLocation) return 'Current Location';

        return location.highlightedText || location.value || '';
      },
      [],
    );

    const handleLocationChange =
      /**
       * @param {LocationOption} selection
       */
      async (selection) => {
        // If current location is selected, geocode the coordinates
        if (
          selection?.isCurrentLocation &&
          geolocation.latitude &&
          geolocation.longitude
        ) {
          const geocodedLocation = await geocodeLocation({
            lat: geolocation.latitude,
            long: geolocation.longitude,
          });

          if (geocodedLocation) {
            onChange?.(geocodedLocation);
            return;
          }
        }

        onChange?.(selection || null);
      };

    const handleFocus = (openOptionsButtonRef) => {
      // Use Headless UI's imperative API to open the combobox
      // when we have location available (to show Current Location option)
      // Only auto-open if there's no value selected yet (prevents clearing on re-focus)
      if (hasLocationAvailable && !value && openOptionsButtonRef.current) {
        openOptionsButtonRef.current.click();
      }
    };

    return (
      <SearchCombobox
        value={value}
        onChange={onChange}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        debounceWait={debounceWait}
        emptyText={emptyText}
        showError={showError}
        fetchOptionsFunction={searchLocations}
        getInputDisplayValue={
          getInputDisplayValue || getInputDisplayValueFallback
        }
        onFocus={handleFocus}
        autoHighlightFirstMatch={false}
        CustomOptions={() => (
          <LocationCustomOptions
            geolocation={geolocation}
            isLocationDisabled={isLocationDisabled}
            isGettingLocation={isGettingLocation}
            showCurrentLocation={showCurrentLocation}
          />
        )}
        getOptionLabel={getOptionLabel}
        getOptionKey={getOptionKey}
        customOnChange={handleLocationChange}
        name={name}
        ref={ref}
      />
    );
  },
);

export default LocationSearchCombobox;
